import { useState, useEffect } from "react"
import { useSignInWithEmailAndPassword, useAuthState } from "react-firebase-hooks/auth"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import CircularProgress from "@mui/material/CircularProgress"
import { auth } from "@/lib/firebase"
import { useSnackbar } from "@/hooks/useSnackbar"

export const Route = createFileRoute("/auth/")({
  component: LoginPage
})

const loginErrorMessage = (code) => {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Incorrect email or password"
    case "auth/too-many-requests":
      return "Too many sign-in attempts. Please wait a moment and try again."
    case "auth/user-disabled":
      return "This account has been disabled. Contact support if you think this is wrong."
    case "auth/network-request-failed":
      return "Couldn't reach Firebase. Check your connection and retry."
    case "auth/invalid-email":
      return "That email address doesn't look right."
    default:
      return "Sign-in failed. Please try again in a moment."
  }
}

function LoginPage() {
  const [existingUser] = useAuthState(auth)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [signInWithEmailAndPassword, user, loading, error] = useSignInWithEmailAndPassword(auth)
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const isRedirecting = !!user || !!existingUser

  useEffect(() => {
    if (user || existingUser) {
      navigate({ to: "/core", replace: true })
    }
  }, [user, existingUser, navigate])

  useEffect(() => {
    if (error) {
      showSnackbar({
        message: loginErrorMessage(error.code),
        severity: "error"
      })
    }
  }, [error, showSnackbar])

  let buttonContent = "Login"
  if (loading) {
    buttonContent = <CircularProgress size={24} color="inherit" />
  } else if (isRedirecting) {
    buttonContent = "Redirecting…"
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (loading || isRedirecting) return
    const trimmedEmail = email.trim()
    if (!trimmedEmail || !password) {
      showSnackbar({ message: "Enter your email and password", severity: "error" })
      return
    }
    signInWithEmailAndPassword(trimmedEmail, password)
  }

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        bgcolor: "secondary.light",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >
      <Stack
        component="form"
        onSubmit={handleSubmit}
        gap={2}
        sx={{
          width: 300,
          py: 2,
          px: 3,
          borderRadius: 1,
          boxShadow: 1,
          bgcolor: "background.paper"
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <img src="/legalware.png" width={100} height={70} alt="legalware company logo" />
          <Typography variant="h3" color="secondary">
            legalware
          </Typography>
        </div>
        <TextField
          id="email"
          type="email"
          label="Email"
          variant="outlined"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          id="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" variant="contained" disabled={loading || isRedirecting}>
          {buttonContent}
        </Button>
        <Typography
          variant="button"
          component={Link}
          to="/auth/forgot-password"
          sx={{ textDecoration: "none" }}
        >
          Forgot Password?
        </Typography>
      </Stack>
    </Stack>
  )
}
